import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import resourcesToBackend from 'i18next-resources-to-backend';

// Utils
import getClientConfig from '../utils/getClientConfig';
import { DEFAULT_LOCALE, DEFAULT_LOCALE_LANGUAGE_CODE } from '../../common/cms/cmsLocaleConstants';
import { getLanguageCodeFromLocale } from '../../common/cms/localeValidationUtils';
import globalLogger from '../logger';
import { configureCdnBackend } from './cdnBackend';

const config = getClientConfig();
const logger = globalLogger.createChannel('i18n');

// SimpleLocalize CDN configuration
const PROJECT_TOKEN = config?.simpleLocalize?.projectToken;
const CDN_BASE_URL = config?.simpleLocalize?.cdnBaseUrl;
const ENVIRONMENT = config?.simpleLocalize?.environment;

type LocaleModule = {
    default: Record<string, Record<string, unknown>>;
};

const localeLoaders: Record<string, () => Promise<LocaleModule>> = {
    en: () => import(/* webpackChunkName: "locales-en" */ '../../common/locales/en'),
    es: () => import(/* webpackChunkName: "locales-es" */ '../../common/locales/es'),
    fr: () => import(/* webpackChunkName: "locales-fr" */ '../../common/locales/fr'),
    de: () => import(/* webpackChunkName: "locales-de" */ '../../common/locales/de'),
    pt: () => import(/* webpackChunkName: "locales-pt" */ '../../common/locales/pt-br'),
    zh: () => import(/* webpackChunkName: "locales-zh" */ '../../common/locales/zh-cn'),
    ru: () => import(/* webpackChunkName: "locales-ru" */ '../../common/locales/ru'),
    pl: () => import(/* webpackChunkName: "locales-pl" */ '../../common/locales/pl'),
    ja: () => import(/* webpackChunkName: "locales-ja" */ '../../common/locales/ja'),
    nl: () => import(/* webpackChunkName: "locales-nl" */ '../../common/locales/nl'),
    it: () => import(/* webpackChunkName: "locales-it" */ '../../common/locales/it'),
    ko: () => import(/* webpackChunkName: "locales-ko" */ '../../common/locales/ko'),
};

const loadFallbackNamespace = async (namespace: string) => {
    const fallbackLoader = localeLoaders[DEFAULT_LOCALE_LANGUAGE_CODE];

    if (!fallbackLoader) {
        throw new Error(
            `Missing fallback i18n loader for default locale "${DEFAULT_LOCALE}" (language code "${DEFAULT_LOCALE_LANGUAGE_CODE}").`,
        );
    }

    const fallbackModule = await fallbackLoader();

    return fallbackModule.default?.[namespace];
};

const loadLocaleNamespace = async (localeOrLanguageCode: string, namespace: string) => {
    const languageCode = getLanguageCodeFromLocale(localeOrLanguageCode || DEFAULT_LOCALE);
    const loader = localeLoaders[languageCode] ?? localeLoaders[DEFAULT_LOCALE_LANGUAGE_CODE];

    if (!loader) {
        throw new Error(
            `Missing i18n loader for locale "${localeOrLanguageCode}" (language code "${languageCode}") and fallback "${DEFAULT_LOCALE_LANGUAGE_CODE}".`,
        );
    }

    const module = await loader();
    const resource = module.default?.[namespace];

    if (resource) return resource;

    if (languageCode !== DEFAULT_LOCALE_LANGUAGE_CODE) {
        const fallbackResource = await loadFallbackNamespace(namespace);
        if (fallbackResource) return fallbackResource;
    }

    throw new Error(
        `Missing i18n namespace "${namespace}" for locale "${localeOrLanguageCode}" (language code "${languageCode}").`,
    );
};

// Common configuration shared between web and Electron
export const commonConfig = {
    fallbackLng: DEFAULT_LOCALE_LANGUAGE_CODE,
    debug: process.env.NODE_ENV === 'development' ? true : false,
    load: 'languageOnly' as const,
    interpolation: {
        // Translations in this project use single-brace placeholders like {planName}
        prefix: '{',
        suffix: '}',
        // React already escapes values; keep false to allow HTML when intentionally used
        escapeValue: false,
    },
    react: {
        transSupportBasicHtmlNodes: false,
        useSuspense: false,
    },
    ns: [
        'checkout',
        'settings',
        'userInterface',
        'appHeader',
        'notifications',
        'common',
        'elements',
        'sharing',
        'toolbar',
        'popup',
        'canvas',
        'auth',
        'onboarding',
        'modal',
        'errors',
        'templates',
        'segmentPicker',
        'segments',
        'commonBoardTitles',
    ],

    // Return keys (not null/empty) when translations are missing, enabling parseMissingKeyHandler
    returnEmptyString: false,
    returnNull: false,
    saveMissing: false,

    // Return defaultValue when translation is missing, otherwise return the key
    parseMissingKeyHandler: (key: string, defaultValue?: string) => defaultValue || key,
};

// Track whether we're using CDN backend
let isUsingCdnBackend = false;

// Initialize i18n with local backend - this promise should be awaited before rendering
// @ts-ignore - VS Code language server incorrectly resolves i18next types, but tsc compiles fine
export const i18nInitPromise = i18n
    .use(initReactI18next)
    .use(resourcesToBackend(loadLocaleNamespace))
    .init(commonConfig)
    .catch((error: unknown) => {
        // Log error but don't throw - app will use fallback language
        logger.error('Failed to initialize i18n:', error);
    });

/**
 * Switch i18n backend to use CDN (SimpleLocalize)
 */
export const switchToCdnBackend = async (): Promise<void> => {
    if (isUsingCdnBackend) return;

    const cdnConfig = {
        projectToken: PROJECT_TOKEN || '',
        cdnBaseUrl: CDN_BASE_URL || '',
        environment: ENVIRONMENT || '',
    };

    await configureCdnBackend(i18n, cdnConfig, commonConfig);

    isUsingCdnBackend = true;
};

/**
 * Check if currently using CDN backend
 */
export const isUsingCdn = (): boolean => isUsingCdnBackend;

/**
 * Load a specific language
 */
export const loadLanguage = (language: string): Promise<void> => {
    return new Promise<void>((resolve) => {
        i18n.loadLanguages(language, () => resolve());
    });
};

export default i18n;
